import { useEffect } from 'react';
import { useBrandTheme } from './BrandThemeContext';
import { useBrand } from './useBrand';

/**
 * Writes the brand into the document head (CNPWKS-0007): `theme-color`, the favicon and the Google
 * Fonts stylesheets for body and heading (never mono, C9). Renders nothing.
 */
export function BrandHead({ scheme }: { scheme: 'light' | 'dark' }) {
  const { brandTheme } = useBrandTheme();
  const { faviconSvg, faviconPng } = useBrand();
  const themeColor = brandTheme.modes[scheme].appBar;
  const fontUrls = [
    ...new Set(
      [brandTheme.fonts.body.cssUrl, brandTheme.fonts.heading.cssUrl].filter(
        (url): url is string => url !== null,
      ),
    ),
  ].join('\n');

  useEffect(() => {
    const metas = document.head.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
    metas.forEach((meta, index) => {
      if (index > 0) meta.remove();
    });
    let meta = metas[0];
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = themeColor;
  }, [themeColor]);

  useEffect(() => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const href = faviconSvg ?? faviconPng;
    if (href === null) {
      // No brand icon (platform-default included): back to the empty icon, never a stale one.
      link.href = 'data:,';
      link.removeAttribute('type');
    } else {
      link.href = href;
      link.type = faviconSvg !== null ? 'image/svg+xml' : 'image/png';
    }
  }, [faviconSvg, faviconPng]);

  useEffect(() => {
    if (fontUrls === '') return;
    const links = fontUrls.split('\n').map((url) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-brand-font', '');
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach((link) => link.remove());
  }, [fontUrls]);

  return null;
}
